/**
 * InvitePage Component
 *
 * Main entry point for magic link invitations.
 * Validates token and routes to appropriate screen based on validation result.
 *
 * Flow:
 * 1. Token validation (loading state)
 * 2. Error handling (if validation fails)
 * 3. Success transition to event details (Story 4.3)
 * 4. Handle confirmed/cancelled events (Story 4.6, 4.7)
 * 5. Detect edit mode for returning guests (Story 4.6)
 * 6. Availability selection and submission (Story 4.4, 4.5, 4.6)
 */

import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAction } from 'convex/react';
import { api } from '@catchup/convex/convex/_generated/api';
import type { ValidateMagicLinkResult } from '@catchup/convex/convex/sms';
import { LoadingScreen } from '../components/LoadingScreen';
import { ErrorScreen } from '../components/ErrorScreen';
import { EventDetails } from '../components/EventDetails';
import { ConfirmDecline } from '../components/ConfirmDecline';
import { AvailabilityPicker, AvailabilityWindow } from '../components/AvailabilityPicker';
import SuccessScreen from '../components/SuccessScreen';

type Step = 'loading' | 'error' | 'details' | 'pick' | 'consent' | 'success' | 'confirm';
type ParticipantWithMessagingConsent = ValidateMagicLinkResult extends infer Result
  ? Result extends { participant: infer Participant }
    ? Participant & {
      messagingConsent?: {
        grantedAt?: number;
        declinedAt?: number;
      };
    }
    : never
  : never;

const updateMessagingConsentAction = api.participantActions.updateMessagingConsent;

function hasMessagingConsentDecision(participant: ParticipantWithMessagingConsent) {
  return Boolean(
    participant.messagingConsent?.grantedAt || participant.messagingConsent?.declinedAt
  );
}

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string>('');
  const [errorCode, setErrorCode] = useState<'EXPIRED' | 'INVALID' | 'EVENT_NOT_FOUND' | 'PARTICIPANT_NOT_FOUND' | undefined>();
  const [canRetryValidation, setCanRetryValidation] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  // New states for availability selection and edit mode (Story 4.6)
  const [selectedSlots, setSelectedSlots] = useState<AvailabilityWindow[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Validate magic link token (using action since it requires JWT verification)
  const [validation, setValidation] = useState<ValidateMagicLinkResult | undefined>(undefined);
  const validateMagicLink = useAction(api.sms.validateMagicLink);

  // Action for submitting/updating availability (Story 4.5, 4.6)
  // Note: Must use useAction because updateGuestAvailability requires Node.js APIs (JWT, crypto)
  const updateGuestAvailability = useAction(api.participantActions.updateGuestAvailability);
  const updateMessagingConsent = useAction(updateMessagingConsentAction);
  const [isSavingMessagingConsent, setIsSavingMessagingConsent] = useState(false);

  // Trigger validation when token is available
  useEffect(() => {
    if (token) {
      let isActive = true;
      const startTime = Date.now();

      setValidation(undefined);
      setCanRetryValidation(false);
      setError('');
      setErrorCode(undefined);
      setStep('loading');

      validateMagicLink({ token })
        .then((result) => {
          if (!isActive) return;
          setValidation(result);
        })
        .catch((error: unknown) => {
          if (!isActive) return;

          const elapsed = Date.now() - startTime;
          const details = error instanceof Error
            ? { elapsed, name: error.name }
            : { elapsed, name: 'UnknownError' };
          console.error('[InvitePage] Invite validation failed', details);

          setError("We couldn't check this invite. Please try again.");
          setErrorCode(undefined);
          setCanRetryValidation(true);
          setStep('error');
        });

      return () => {
        isActive = false;
      };
    }
  }, [token, validationAttempt, validateMagicLink]);

  // Handle validation result and route to appropriate step
  useEffect(() => {
    if (validation === undefined) {
      // Still loading
      setStep('loading');
    } else if ('error' in validation) {
      // Validation failed
      setError(validation.error);
      setErrorCode(validation.code);
      setStep('error');
    } else {
      // Validation succeeded - check event status
      // Story 4.7: Check if event is finalized (confirmed status)
      if (validation.event.status === 'confirmed') {
        // Event finalized by organizer - show confirm/decline screen
        setStep('confirm');
      } else if (validation.event.status === 'cancelled') {
        // Event cancelled - show error (AC-4.6.5)
        setError('This hangout has been cancelled.');
        setErrorCode('INVALID');
        setStep('error');
      } else {
        // Event not finalized yet - handle availability flow
        if (validation.participant.hasResponded) {
          // EDIT MODE: Pre-load previous availability (AC-4.6.1, AC-4.6.2)
          // Transform { startTime, endTime } to { start, end } format
          const slots = (validation.participant.availabilitySlots || []).map(slot => ({
            start: slot.startTime,
            end: slot.endTime,
          }));
          setSelectedSlots(slots);
          setIsEditing(true);
          setStep('pick'); // Go directly to picker (skip details)
        } else {
          // FIRST-TIME MODE: Show event details first
          setIsEditing(false);
          setStep('details');
        }
      }
    }
  }, [validation]);

  // Handle availability submission (Story 4.5, 4.6)
  const handleSubmit = async () => {
    if (!token || !validation || !('event' in validation) || !('participant' in validation)) {
      console.error('Missing token or validation data for submission.');
      setError('An internal error occurred. Please try again.');
      setErrorCode(undefined);
      setStep('error');
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const result = await updateGuestAvailability({
        token,
        availabilitySlots: selectedSlots,
        timezone,
      });

      if (result.success) {
        setStep(hasMessagingConsentDecision(validation.participant) ? 'success' : 'consent');
      } else {
        // Map specific mutation errors to existing error codes if applicable
        const code = result.error === 'Link expired' ? 'EXPIRED' : undefined;
        setError(result.error || 'Failed to update availability.');
        setErrorCode(code);
        setStep('error');
      }
    } catch (err: unknown) {
      console.error('Error submitting availability:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred during submission.';
      setError(errorMessage);
      setErrorCode(undefined);
      setStep('error');
    }
  };

  const handleMessagingConsent = async (allowUpdates: boolean) => {
    if (!token) {
      setStep('success');
      return;
    }

    setIsSavingMessagingConsent(true);

    try {
      await updateMessagingConsent({
        token,
        consent: {
          sms: allowUpdates,
          email: allowUpdates,
          source: 'pwa_after_response',
        },
      });
    } catch (err: unknown) {
      console.error('Error updating messaging consent:', err);
    } finally {
      setIsSavingMessagingConsent(false);
      setStep('success');
    }
  };

  // Render appropriate screen based on step
  if (step === 'loading') {
    return <LoadingScreen />;
  }

  if (step === 'error') {
    return (
      <ErrorScreen
        message={error}
        code={errorCode}
        onRetry={canRetryValidation ? () => setValidationAttempt((attempt) => attempt + 1) : undefined}
      />
    );
  }

  if (step === 'details') {
    // Story 4.3 - Event Details View (First-time user)
    if (validation && 'event' in validation) {
      // Ensure required fields have defaults and cast types
      const meetingFormat = validation.event.meetingFormat as 'in_person' | 'virtual' | 'undecided' | undefined;
      const eventData = {
        ...validation.event,
        timeframe: validation.event.timeframe || 'this_week',
        duration: validation.event.duration || 'coffee',
        meetingFormat,
      };
      return (
        <EventDetails
          event={eventData}
          participantName={validation.participant.displayName}
          onContinue={() => setStep('pick')}
        />
      );
    }
    // Fallback (should never reach here after successful validation)
    return null;
  }

  if (step === 'pick') {
    // Story 4.4 & 4.6 - Availability Picker (First-time or Edit mode)
    if (validation && 'event' in validation) {
      // Ensure required fields have defaults
      const eventData = {
        ...validation.event,
        timeframe: validation.event.timeframe || 'this_week',
        duration: validation.event.duration || 'coffee',
      };
      return (
        <AvailabilityPicker
          event={eventData}
          selectedSlots={selectedSlots}
          onSlotsChange={setSelectedSlots}
          onSubmit={handleSubmit}
          isEditing={isEditing}
          mode={validation.event.mode as 'single' | 'group' | undefined}
          organizerName={validation.event.organizerName}
        />
      );
    }
    return null;
  }

  if (step === 'success') {
    // Story 4.5 & 4.6 - Submission Success Screen
    if (validation && 'event' in validation) {
      return (
        <SuccessScreen
          event={validation.event}
          message={
            isEditing
              ? 'Your availability has been updated!' // AC-4.6.4
              : "Thanks! We'll let you know when a time is confirmed." // Story 4.5
          }
        />
      );
    }
    return null;
  }

  if (step === 'consent') {
    if (validation && 'event' in validation) {
      return (
        <SuccessScreen
          event={validation.event}
          message={
            isEditing
              ? 'Your availability has been updated!'
              : "Thanks! We'll let you know when a time is confirmed."
          }
          messagingConsentPrompt={{
            isSubmitting: isSavingMessagingConsent,
            onAllow: () => handleMessagingConsent(true),
            onDecline: () => handleMessagingConsent(false),
          }}
        />
      );
    }
    return null;
  }

  if (step === 'confirm') {
    // Story 4.7 - Confirm/Decline Screen
    if (validation && 'event' in validation && token) {
      return (
        <ConfirmDecline
          token={token}
          event={validation.event}
          participant={validation.participant}
        />
      );
    }
    // Fallback (should never reach here)
    return null;
  }

  return null; // Default fallback
}
